/**
 * Messages v2 design tokens.
 *
 * The page adopts the Schedule v2 visual language so the two consoles read as
 * one product — see `pages/Schedule/SCHEDULE_V2_DESIGN.md` for the source of
 * these values and `MESSAGES_V2_DESIGN.md` §10 for how they map here.
 *
 * Typography comes from the `.behuman-v2-scope` / `.behuman-v2-mulish` classes
 * in `src/index.css`: Plus Jakarta Sans for text, Mulish for controls.
 *
 * Only the shared class strings live here. The palette itself is not exported
 * as a token object: Tailwind's JIT only emits classes it can find as complete
 * literals, so components must write `text-[#6B7280]` rather than interpolate a
 * constant. `MESSAGES_V2_DESIGN.md` §7b is the canonical list of those values.
 */

/** Panel chrome — the 18px white card Schedule uses for its calendar surface. */
export const CARD_CLASS =
  'rounded-[18px] border border-[#E7E9EC] bg-white shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]'

// The coral primary button is shared with Provider Operations, so it lives in `components/ui`.
export { PRIMARY_BUTTON_PROPS } from '@/components/ui/primaryButton.tokens'

const TOGGLE_BASE =
  'behuman-v2-mulish cursor-pointer rounded-lg border px-4 py-[9px] !text-[13px] !font-bold transition-colors'

// Written out in full rather than interpolated: Tailwind only emits classes it
// can find as complete literals in the source, so `border-[${color}]` would
// compile to nothing.
const TOGGLE_VARIANTS = {
  urgent: {
    on: `${TOGGLE_BASE} border-[#EF4444] bg-[#EF4444] text-white`,
    off: `${TOGGLE_BASE} border-[#F6BFBF] bg-white text-[#EF4444] hover:bg-[#FEE2E2]`,
  },
  followUp: {
    on: `${TOGGLE_BASE} border-[#B45309] bg-[#B45309] text-white`,
    off: `${TOGGLE_BASE} border-[#F3D9A4] bg-white text-[#B45309] hover:bg-[#FEF3C7]`,
  },
} as const

/**
 * The Urgent / Follow-up toggles in the new-thread modal. They are on/off
 * switches rather than plain buttons, so the pressed state is a solid fill —
 * colour alone never carries the state, `aria-pressed` does.
 */
export function labelToggleClass(active: boolean, tone: keyof typeof TOGGLE_VARIANTS): string {
  return TOGGLE_VARIANTS[tone][active ? 'on' : 'off']
}
